<template>
  <div class="folder-cards">

    <!-- Loading state: 3 skeleton cards -->
    <div v-if="loading" class="folder-cards__grid">
      <div
        v-for="n in 3"
        :key="'skeleton-' + n"
        class="folder-card folder-card--skeleton"
        aria-hidden="true"
      >
        <div class="folder-card__skeleton-title"></div>
        <div class="folder-card__skeleton-bar"></div>
        <div class="folder-card__skeleton-meta"></div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!folders || folders.length === 0" class="text-muted text-center py-4">
      No folder data available.
    </div>

    <!-- Card grid -->
    <div v-else class="folder-cards__grid">
      <div
        v-for="folder in enrichedFolders"
        :key="folder.text"
        class="folder-card"
      >
        <!-- Header: folder name link + task fraction -->
        <div class="d-flex justify-content-between align-items-start mb-2">
          <router-link
            :to="{ name: 'tasks-folder', params: { folder: folder.text } }"
            class="folder-card__title"
          >
            {{ folder.text }}
          </router-link>
          <span class="folder-card__fraction text-muted">
            {{ folder.completed }}&thinsp;/&thinsp;{{ folder.total }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="folder-card__bar-track" role="progressbar" :aria-valuenow="folder.pct" aria-valuemin="0" aria-valuemax="100">
          <div
            class="folder-card__bar-fill"
            :style="{ width: folder.pct + '%', backgroundColor: folder.color }"
          ></div>
        </div>

        <!-- Counts + recency -->
        <div class="d-flex justify-content-between align-items-center mt-2">
          <span class="folder-card__count text-muted">
            {{ folder.completed }} of {{ folder.total }} task{{ folder.total !== 1 ? 's' : '' }} complete
          </span>
          <span
            v-if="folder.recencyText"
            class="folder-card__recency"
            :class="folder.recencyWarning ? 'folder-card__recency--warn' : 'text-muted'"
          >
            {{ folder.recencyText }}
          </span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
// 20260606 -- Copilot (Claude Sonnet 4.6) -- Folder progress card grid replacing folder count table
import moment from 'moment'

export default {
  name: 'FolderCards',

  props: {
    folders: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    enrichedFolders() {
      return this.folders.map(folder => {
        const pct = folder.total > 0 ? Math.round((folder.completed / folder.total) * 100) : 0
        const color = this.progressColor(pct, folder.total)
        const { text: recencyText, warning: recencyWarning } = this.recencyInfo(folder.lastCompleted)
        return { ...folder, pct, color, recencyText, recencyWarning }
      })
    }
  },

  methods: {
    progressColor(pct, total) {
      if (total === 0) return '#9ca3af'
      if (pct >= 80) return '#10b981'
      if (pct >= 40) return '#f59e0b'
      return '#ef4444'
    },

    recencyInfo(lastCompleted) {
      // 20260606 -- Copilot (Claude Sonnet 4.6) -- No completions is as notable as stalled; use same warning style
      if (!lastCompleted) return { text: 'No completions yet', warning: true }

      const daysSince = moment().diff(moment(lastCompleted), 'days')

      if (daysSince === 0) return { text: 'Completed today', warning: false }
      if (daysSince === 1) return { text: 'Yesterday', warning: false }
      if (daysSince <= 14) return { text: `Last completed ${daysSince} days ago`, warning: false }
      return { text: `⚠️ No completions in ${daysSince} days`, warning: true }
    }
  }
}
</script>

<style scoped>
/* 20260606 -- Copilot (Claude Sonnet 4.6) -- Responsive grid and card styles for FolderCards */

.folder-cards__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 576px) {
  .folder-cards__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 992px) {
  .folder-cards__grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.folder-card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  background: #fff;
}

.folder-card__title {
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  color: #212529;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  line-height: 1.3;
}

.folder-card__title:hover {
  color: #0d6efd;
  text-decoration: underline;
}

.folder-card__fraction {
  font-size: 0.8rem;
  white-space: nowrap;
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.folder-card__bar-track {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.folder-card__bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.folder-card__count {
  font-size: 0.8rem;
}

.folder-card__recency {
  font-size: 0.75rem;
  text-align: right;
  margin-left: 0.5rem;
}

.folder-card__recency--warn {
  color: #d97706;
  font-weight: 500;
}

/* Skeleton loading cards */
.folder-card--skeleton {
  pointer-events: none;
}

.folder-card__skeleton-title,
.folder-card__skeleton-bar,
.folder-card__skeleton-meta {
  background: #e9ecef;
  border-radius: 4px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.folder-card__skeleton-title {
  height: 18px;
  width: 60%;
  margin-bottom: 0.75rem;
}

.folder-card__skeleton-bar {
  height: 8px;
  width: 100%;
  margin-bottom: 0.75rem;
}

.folder-card__skeleton-meta {
  height: 14px;
  width: 80%;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
  .folder-card__skeleton-title,
  .folder-card__skeleton-bar,
  .folder-card__skeleton-meta {
    animation: none;
  }

  .folder-card__bar-fill {
    transition: none;
  }
}
</style>
